import { useMemo, useState } from 'react'

import { Box, Skeleton, Text } from '@factorialco/deprecated-design-system'
import { FileSigned } from '@factorialco/f0-react/icons/app'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import FormFieldEditModal from 'modules/expenses/components/FormFieldEditModal'
import FormFieldsList from 'modules/expenses/components/FormFieldsList'
import {
  CustomizationField,
  fieldsFor,
  useExpensesCustomization,
} from 'modules/expenses/hooks/useExpensesCustomization'
import SectionInfo from 'modules/expenses/pages/settings/ExpensePolicy/Components/_SectionInfo'

interface FormTypeSectionProps {
  title: string
  description: string
  fields: CustomizationField[]
  isLoading?: boolean
  onClickItemSettings: (field: CustomizationField) => void
}

const FormTypeSection = ({
  title,
  description,
  fields,
  isLoading = false,
  onClickItemSettings,
}: FormTypeSectionProps) => {
  if (isLoading) {
    return (
      <Box flexDirection="column" gap="s16">
        <Box flexDirection="column" gap="s4">
          <Skeleton width="s96" height="s24" isLoading />
          <Skeleton width="full" height="s16" isLoading />
        </Box>
        <Skeleton width="full" height="s64" isLoading />
      </Box>
    )
  }

  return (
    <Box flexDirection="column" gap="s16">
      <Box flexDirection="column" gap="s4">
        <Text.Normal weight="semibold" size="300">
          {title}
        </Text.Normal>
        <Text.Normal size="200" color="grey600">
          {description}
        </Text.Normal>
      </Box>
      <FormFieldsList fields={fields} onClickItemSettings={onClickItemSettings} />
    </Box>
  )
}

interface Props {
  hasPerDiemsFeature: boolean
}

const FormsRequirements = ({ hasPerDiemsFeature }: Props) => {
  const { fields, loading } = useExpensesCustomization()
  const [selectedField, setSelectedField] = useState<CustomizationField | null>(null)

  const expenseFields = useMemo(() => fieldsFor(fields || [], 'expense'), [fields])
  const mileageFields = useMemo(() => fieldsFor(fields || [], 'mileage'), [fields])
  const perdiemFields = useMemo(
    () => (hasPerDiemsFeature ? fieldsFor(fields || [], 'perdiem') : []),
    [fields, hasPerDiemsFeature]
  )

  const onClickItemSettings = (field: CustomizationField) => {
    track(trackingEvents.expenses.goToExpensesFormFieldEdit, {
      expense_type: field.resourceType,
      field_name: field.identifier,
    })

    setSelectedField(field)
  }

  const onCloseModal = () => {
    setSelectedField(null)
  }

  return (
    <Box flexDirection="column" gap="s24" paddingX="s24" flexGrow>
      <SectionInfo icon={FileSigned} title={i18n.t('expenses_v2.settings.forms.title')} />

      <Box flexDirection="column" gap="s32">
        <FormTypeSection
          title={i18n.t('expenses_v2.settings.forms.types.expense')}
          description={i18n.t('expenses_v2.settings.forms.fields_customization.description', {
            type: i18n.t('expenses_v2.settings.forms.types.expense'),
          })}
          fields={expenseFields}
          isLoading={loading}
          onClickItemSettings={onClickItemSettings}
        />
        <FormTypeSection
          title={i18n.t('expenses_v2.settings.forms.types.mileage')}
          description={i18n.t('expenses_v2.settings.forms.fields_customization.description', {
            type: i18n.t('expenses_v2.settings.forms.types.mileage'),
          })}
          fields={mileageFields}
          isLoading={loading}
          onClickItemSettings={onClickItemSettings}
        />
        {hasPerDiemsFeature && (
          <FormTypeSection
            title={i18n.t('expenses_v2.settings.forms.types.per_diem')}
            description={i18n.t('expenses_v2.settings.forms.fields_customization.description', {
              type: i18n.t('expenses_v2.settings.forms.types.per_diem'),
            })}
            fields={perdiemFields}
            isLoading={loading}
            onClickItemSettings={onClickItemSettings}
          />
        )}
      </Box>

      {selectedField && (
        <FormFieldEditModal field={selectedField} allFields={fields || []} onClose={onCloseModal} />
      )}
    </Box>
  )
}

export default FormsRequirements
